/**
 * "Aguardando data (N)" (spec 039, adendo 2): na aba da agenda por recurso, os
 * tickets que o ERP sabe que estão agendados (a etapa é de agendamento) e não
 * para quando (nenhum compromisso em aberto). O bot da Blip só pergunta o
 * período, e ninguém grava o dia num lugar legível: o lugar de marcar o dia
 * passa a ser o ERP.
 *
 * - Quem entra e em que ordem decide a API (`GET /agenda/awaiting-schedule`,
 *   com as permissões da busca da agenda): há mais tempo na etapa primeiro.
 * - Segue o switch "Mostrar clientes ocultos" da aba; o oculto leva a marca e
 *   o nº dele não é link (o detalhe é 404).
 * - O número do título é o TOTAL da API; passando do teto (300), a tela diz.
 * - "Agendar" abre o drawer do "Agendar" com o ticket escolhido e a janela do
 *   período do bot. Agendar invalida a agenda inteira: a linha sai daqui e o
 *   evento entra no calendário.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { CalendarCheck } from 'lucide-react';
import { BOT_PERIOD_LABELS, type AgendaAwaitingItem } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Badge, Skeleton } from '@/components/ui/misc';
import { formatElapsed } from '@/features/work/work-format';
import { awaitingScheduleQuery, errorMessage } from './agenda-queries';
import { HiddenTicketBadge } from './hidden-ticket-mark';

function AwaitingRow({
  item,
  now,
  onSchedule,
}: {
  item: AgendaAwaitingItem;
  now: Date;
  onSchedule: (item: AgendaAwaitingItem) => void;
}) {
  const label = item.resourceLabel ?? item.title;
  return (
    <li className="flex items-start gap-2 px-3 py-2">
      <div className="min-w-0 flex-1 text-sm">
        <div className="truncate">
          <span className="font-semibold">{label}</span>{' '}
          {item.hidden ? (
            <span className="text-muted-foreground">#{item.ticketNumber}</span>
          ) : (
            <Link
              to="/tickets/$ticketNumber"
              params={{ ticketNumber: String(item.ticketNumber) }}
              className="text-primary hover:text-primary-pressed hover:underline"
            >
              #{item.ticketNumber}
            </Link>
          )}
        </div>
        <div className="truncate text-xs text-muted-foreground" title={item.clientName}>
          {item.clientName}
        </div>
        {/* a coluna é estreita: o texto inteiro fica no title */}
        <div
          className="truncate text-xs text-muted-foreground"
          title={`${item.deskName} · ${item.stageName}`}
        >
          {item.deskName} · {item.stageName}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
          <span
            className="text-muted-foreground"
            title={`Na etapa desde ${new Date(item.stageEnteredAt).toLocaleString('pt-BR')}`}
          >
            há {formatElapsed(now.getTime() - new Date(item.stageEnteredAt).getTime())}
          </span>
          {item.botPeriod ? (
            <Badge variant="info" title="Período que o cliente escolheu no bot">
              {BOT_PERIOD_LABELS[item.botPeriod]}
            </Badge>
          ) : null}
          {item.hidden ? <HiddenTicketBadge /> : null}
        </div>
      </div>
      <Button
        type="button"
        size="xs"
        variant="outline"
        onClick={() => onSchedule(item)}
        aria-label={`Agendar ${label} #${item.ticketNumber}`}
      >
        <CalendarCheck />
        Agendar
      </Button>
    </li>
  );
}

export function AwaitingScheduleList({
  includeHidden,
  onSchedule,
}: {
  /** o switch "Mostrar clientes ocultos" da aba */
  includeHidden: boolean;
  onSchedule: (item: AgendaAwaitingItem) => void;
}) {
  const q = useQuery(awaitingScheduleQuery({ includeHidden }));
  const items = q.data?.items ?? [];
  const total = q.data?.total ?? null;
  const now = new Date();

  return (
    <section
      aria-labelledby="awaiting-schedule-title"
      className="rounded-lg border bg-card shadow-xs"
    >
      <header className="flex items-center gap-2 border-b border-divider px-3 py-2.5">
        <h2 id="awaiting-schedule-title" className="flex-1 text-sm font-semibold">
          Aguardando data{total !== null ? ` (${total})` : ''}
        </h2>
      </header>
      {q.isPending ? (
        <div className="space-y-2 p-3" aria-busy>
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-5/6" />
        </div>
      ) : q.isError ? (
        <p className="px-3 py-3 text-sm text-destructive">{errorMessage(q.error)}</p>
      ) : items.length === 0 ? (
        <p className="px-3 py-3 text-sm text-muted-foreground">Nenhum ticket aguardando data.</p>
      ) : (
        <ul className="max-h-[28rem] divide-y divide-divider overflow-y-auto">
          {items.map((item) => (
            <AwaitingRow key={item.ticketNumber} item={item} now={now} onSchedule={onSchedule} />
          ))}
        </ul>
      )}
      {total !== null && total > items.length ? (
        <p className="border-t border-divider px-3 py-2 text-xs text-muted-foreground">
          Mostrando os {items.length} há mais tempo na etapa, de {total}.
        </p>
      ) : null}
    </section>
  );
}
